// @LTG-ID: data/journals/selling-a-professional-services-firm
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0108
// @CHANGED: 2026-07-06 (CHG-0108 · Journal port completion, articles 21-40)
// @OWNER: web
//
// Article 38 of 40. Source: batch MD at /app/_ltg_content_batches/batch_3_4/.
// Byline: LTG Team (per CHG-0107.6 §17.3 convention).
// Section: Industry-Specific (per CHG-0108 D4 orchestrator adjudication).

export const BRIEF = {
    SLUG: "selling-a-professional-services-firm",
    FRONTMATTER: {
        number: "75",
        section: "Industry-Specific",
        sectionHref: "/journals#industry-specific",
        sectionAnchor: "industry-specific",
        title: "Selling a Professional Services Firm: Agencies, Consultancies, and Practices Where the Product Is People",
        deck: "Professional services firms sell on the transferability of client relationships, recurring engagement structures like retainers, and delivery capacity beyond the founder.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-07-02",
        publishedHuman: "July 2, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 492,
        timeRequired: "PT3M",
        keywords: "selling a professional services firm, selling an agency, consulting firm valuation, selling a service business clients",
        articleSection: "Industry-Specific",
        articleBodyPreview: "Professional services firms sell on the transferability of client relationships, recurring engagement structures like retainers, and delivery capacity beyond the founder. Because the founder's personal relationships are often the firm's gravity, deals frequently include earnouts and transition periods. Retainer revenue and a senior team materially strengthen both price and terms. In a services firm, the inventory goes home every night and the product is trust. Selling one is therefore the purest",
        complianceNote: "This article is educational and does not constitute legal or financial advice. Regulated professions involve ownership and licensure rules that vary by profession and state.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "owner-dependence-business-value",
        "recurring-revenue-business-value",
        "earnout-business-sale"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Selling a Professional Services Firm: Agencies to Practices",
        canonical: "https://letstalkgrowth.com/journals/selling-a-professional-services-firm",
        ogImage: "/journals/og/selling-a-professional-services-firm.jpg",
    },
    BODY_MARKDOWN: `Professional services firms sell on the transferability of client relationships, recurring engagement structures like retainers, and delivery capacity beyond the founder. Because the founder's personal relationships are often the firm's gravity, deals frequently include earnouts and transition periods. Retainer revenue and a senior team materially strengthen both price and terms.

In a services firm, the inventory goes home every night and the product is trust. Selling one is therefore the purest test of the question underneath every article in this series: what, exactly, transfers?

## The gravity problem

Clients of agencies, consultancies, and professional practices hire people before they hire firms, and in founder-led firms the person they hired is usually the founder. Buyers price this precisely. Their diligence maps every significant client to its relationships: who sold the engagement, who delivers it, who the client calls when unhappy. Where every arrow points at the founder, the buyer is purchasing an introduction, not a book of business, and the deal structure says so: heavier earnouts, longer founder lock-ins, and more price contingent on retention. Where relationships distribute across a senior team, the same revenue commands a stronger price on cleaner terms. The multi-year preparation is the deliberate migration of client gravity from founder to firm: second leads on every account, delivery run by the team, the founder receding to strategy and then to ceremony.

## Revenue quality in services form

The recurring-revenue hierarchy from earlier in this series translates directly. Retainers and multi-year service agreements anchor the top; annuity-like recurring engagements, ongoing accounting work, managed services, maintenance-of-record relationships, sit close behind; repeat project clients follow; pure one-off project revenue trails. Firms that convert even a fraction of project relationships to retainer structures change their buyer conversation, and utilization discipline plus documented delivery methodology, the firm's playbooks rather than its heroics, tell the buyer margins will survive the founder's exit.

## The regulated professions footnote that is not a footnote

Firms in licensed professions, CPA practices, law firms, engineering and architecture in some states, sell inside ownership and licensure rules that constrain who may buy and how deals are structured. CPA firm ownership rules, attorney ethics rules on practice sales, and state licensure requirements each reshape the buyer pool and the paperwork. None of this prevents sales; these practices change hands constantly, often to successor professionals or consolidating firms, but the rules belong in the plan from day one, with counsel and the profession's regulator's guidance in hand.

## What the strong services exit looks like

Distributed client relationships with a senior team the buyer wants to keep, retainer-weighted revenue with retention data to prove it, documented methodology, clean provable financials, and a founder whose reduced role has already been rehearsed rather than merely promised. Deals for such firms still commonly include transitions and modest earnouts, that is the industry's texture, but the guaranteed portion dominates, which, as our earnout article argued, is the number that matters.`,
    FAQ: [
        { question: "Why do services firm deals include so many earnouts?", answer: "Because the core asset, client relationships, must prove it survives the founder's exit. Earnouts and transition periods bridge that uncertainty. Firms that distribute relationships across a team before selling shift more of the price into the guaranteed portion." },
        { question: "What is the most valuable revenue in an agency or consultancy?", answer: "Retainer and multi-year recurring engagements, backed by retention history. They demonstrate that clients buy the firm, not just a project, and they transfer to a new owner in a way one-off projects never can." },
        { question: "Can CPA and law practices be sold like other businesses?", answer: "They sell regularly, but within profession-specific ownership, ethics, and licensure rules that shape who may buy and how deals are structured. Engage counsel familiar with your profession's rules at the start of planning." }
    ],
    SOURCES: [
    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal \u2192",
        microLine: "\u00b7 7-DAY DELIVERY \u00b7 KGOB METHODOLOGY, NC CPA #30420 \u00b7",
    },
    READ_NEXT: [
        {
            tag: "Value Drivers",
            title: "Owner Dependence: The Biggest Hidden Discount on Your Business's Value",
            meta: "3 min · Brief 45",
            to: "/journals/owner-dependence-business-value",
        },
        {
            tag: "Value Drivers",
            title: "Recurring Revenue: Why Buyers Pay More for Businesses That Bill Every Month",
            meta: "3 min · Brief 49",
            to: "/journals/recurring-revenue-business-value",
        },
        {
            tag: "Deal Process",
            title: "Earnouts Explained: Getting Paid Based on the Business's Future Performance",
            meta: "3 min · Brief 62",
            to: "/journals/earnout-business-sale",
        },
    ],
};

export default BRIEF;
